import { useEffect, useId, useRef, useState, type ReactNode } from 'react';

/** Une proposition de la liste. */
export interface Proposition<T> {
  cle: string;
  /** Texte principal (« 62.01Z », « 34170 »). */
  titre: string;
  /** Précision affichée à côté (« Programmation informatique »). */
  detail?: string;
  donnee: T;
}

interface Props<T> {
  id: string;
  libelle: ReactNode;
  /** Texte affiché dans la case. */
  valeur: string;
  /** Frappe libre : la case change, la sélection précédente ne tient plus. */
  onSaisie: (texte: string) => void;
  /** Une proposition choisie. */
  onChoix: (donnee: T) => void;
  rechercher: (texte: string) => Promise<Proposition<T>[]>;
  /** Nombre de caractères avant de chercher (2 par défaut). */
  minimum?: number;
  erreur?: string;
  aide?: ReactNode;
  disabled?: boolean;
  inputMode?: 'text' | 'numeric';
  maxLength?: number;
  placeholder?: string;
}

/**
 * Case de saisie avec recherche dans un référentiel (14/10/2026) — combobox
 * ARIA 1.2 : la liste s'ouvre en tapant, se parcourt aux flèches, Entrée
 * choisit, Échap ferme. La recherche attend une courte pause de frappe et
 * ignore les réponses arrivées dans le désordre.
 */
export function ChampRecherche<T>({
  id,
  libelle,
  valeur,
  onSaisie,
  onChoix,
  rechercher,
  minimum = 2,
  erreur,
  aide,
  disabled,
  inputMode,
  maxLength,
  placeholder,
}: Props<T>) {
  const liste = useId();
  const [ouverte, setOuverte] = useState(false);
  const [propositions, setPropositions] = useState<Proposition<T>[]>([]);
  const [active, setActive] = useState(-1);
  const [etat, setEtat] = useState<'repos' | 'recherche' | 'vide' | 'panne'>('repos');
  const numero = useRef(0);
  const minuterie = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (minuterie.current) clearTimeout(minuterie.current);
    },
    [],
  );

  function lancer(texte: string) {
    if (minuterie.current) clearTimeout(minuterie.current);
    if (texte.trim().length < minimum) {
      setPropositions([]);
      setOuverte(false);
      setEtat('repos');
      return;
    }
    minuterie.current = setTimeout(() => {
      const n = ++numero.current;
      setEtat('recherche');
      rechercher(texte)
        .then((r) => {
          if (n !== numero.current) return;
          setPropositions(r);
          setActive(r.length > 0 ? 0 : -1);
          setOuverte(true);
          setEtat(r.length > 0 ? 'repos' : 'vide');
        })
        .catch(() => {
          if (n !== numero.current) return;
          setPropositions([]);
          setOuverte(true);
          setEtat('panne');
        });
    }, 200);
  }

  function choisir(p: Proposition<T>) {
    numero.current += 1; // une recherche en vol ne rouvrira pas la liste
    onChoix(p.donnee);
    setOuverte(false);
    setPropositions([]);
    setEtat('repos');
  }

  function touche(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!ouverte && propositions.length > 0) setOuverte(true);
      setActive((a) => Math.min(a + 1, propositions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter' && ouverte && active >= 0 && propositions[active]) {
      e.preventDefault();
      choisir(propositions[active]);
    } else if (e.key === 'Escape' && ouverte) {
      e.preventDefault();
      setOuverte(false);
    }
  }

  const idOption = (i: number) => `${liste}-o${i}`;
  const montrer = ouverte && (propositions.length > 0 || etat === 'vide' || etat === 'panne');

  return (
    <div className={`field ref-champ${erreur ? ' has-erreur' : ''}`}>
      <label htmlFor={id}>{libelle}</label>
      <div className="ref-boite">
        <input
          id={id}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={montrer}
          aria-controls={liste}
          aria-activedescendant={montrer && active >= 0 ? idOption(active) : undefined}
          aria-invalid={erreur ? true : undefined}
          aria-describedby={erreur ? `${id}-erreur` : aide ? `${id}-aide` : undefined}
          autoComplete="off"
          value={valeur}
          disabled={disabled}
          inputMode={inputMode}
          maxLength={maxLength}
          placeholder={placeholder}
          onChange={(e) => {
            onSaisie(e.target.value);
            lancer(e.target.value);
          }}
          onKeyDown={touche}
          onFocus={() => {
            if (propositions.length > 0) setOuverte(true);
          }}
          onBlur={() => setOuverte(false)}
        />
        {etat === 'recherche' ? <span className="ref-encours" aria-hidden="true" /> : null}
        <ul id={liste} role="listbox" className="ref-liste" hidden={!montrer}>
          {propositions.map((p, i) => (
            <li
              key={p.cle}
              id={idOption(i)}
              role="option"
              aria-selected={i === active}
              className={i === active ? 'is-active' : undefined}
              // mousedown, pas click : le blur de la case fermerait la liste avant.
              onMouseDown={(e) => {
                e.preventDefault();
                choisir(p);
              }}
              onMouseEnter={() => setActive(i)}
            >
              <strong>{p.titre}</strong>
              {p.detail ? <span>{p.detail}</span> : null}
            </li>
          ))}
          {etat === 'vide' ? (
            <li className="ref-message" role="option" aria-disabled="true" aria-selected={false}>
              Aucun résultat dans la base.
            </li>
          ) : null}
          {etat === 'panne' ? (
            <li className="ref-message" role="option" aria-disabled="true" aria-selected={false}>
              Recherche indisponible pour le moment.
            </li>
          ) : null}
        </ul>
      </div>
      {aide && !erreur ? (
        <p id={`${id}-aide`} className="dos-aide">
          {aide}
        </p>
      ) : null}
      {erreur ? (
        <p id={`${id}-erreur`} className="dos-erreur">
          {erreur}
        </p>
      ) : null}
    </div>
  );
}
